/**
 * Los tipos de canal y lo que cada uno puede hacer (change `conversaciones-coexistence`, tarea 3.1).
 *
 * Hasta ahora había un solo WhatsApp y el front preguntaba `type === "whatsapp"` en cada lugar. Con la
 * Cloud API hay dos, y esas preguntas se contestan acá: "¿es un WhatsApp?", "¿cómo se nombra el hilo?",
 * "¿tiene ventana?". Lo que depende del servidor —qué tipos se pueden dar de alta— sale de
 * `GET /channels/types`; lo demás es fijo por tipo.
 */
import { formatWhatsappPhone, toWhatsappJid } from "./whatsapp-phone";

export type ChannelTypeName = "whatsapp" | "whatsapp_cloud" | "email" | "gmail" | "messenger";

/** Lo que el provider del tipo puede hacer, como lo anuncia el conversation-service (D14). */
export interface ChannelCapabilities {
  /** Tiene una sesión de WhatsApp Web que se conecta con QR. */
  sockets: boolean;
  /** Puede pedir mensajes anteriores a pedido ("Buscar anteriores en WhatsApp"). */
  history: boolean;
  /** Horas de la ventana de servicio; `null` = no hay ventana. */
  serviceWindowHours: number | null;
  /** Tope de envío por tipo de contenido, en MB. */
  maxMediaMb: Partial<Record<string, number>>;
}

export interface ChannelTypeInfo {
  type: string;
  capabilities: ChannelCapabilities;
}

const WHATSAPP_WEB: ChannelCapabilities = { sockets: true, history: true, serviceWindowHours: null, maxMediaMb: {} };
const CLOUD_API: ChannelCapabilities = {
  sockets: false,
  history: false,
  serviceWindowHours: 24,
  maxMediaMb: { image: 5, video: 16, audio: 16, document: 100 },
};

/** Lo que se sabe de cada tipo sin preguntarle al servidor. */
const CAPACIDADES: Record<string, ChannelCapabilities> = {
  whatsapp: WHATSAPP_WEB,
  whatsapp_cloud: CLOUD_API,
};

const LABELS: Record<string, string> = {
  whatsapp: "WhatsApp (QR)",
  whatsapp_cloud: "WhatsApp Business (Coexistence)",
  email: "Email",
  gmail: "Gmail",
  messenger: "Messenger",
};

/** Los tipos del alta, en el orden en que se muestran. */
export const CHANNEL_TYPES: { value: ChannelTypeName; label: string }[] = [
  { value: "whatsapp", label: LABELS.whatsapp },
  { value: "whatsapp_cloud", label: LABELS.whatsapp_cloud },
  { value: "email", label: LABELS.email },
  { value: "gmail", label: LABELS.gmail },
  { value: "messenger", label: LABELS.messenger },
];

export const labelFor = (type: string): string => LABELS[type] ?? type;

/** ¿Es un canal de WhatsApp, por QR o por la Cloud API? */
export const isWhatsappFamily = (type: string | null | undefined): boolean =>
  type === "whatsapp" || type === "whatsapp_cloud";

/** ¿Es un número de la Cloud API de Meta (Coexistence)? */
export const isCloudApi = (type: string | null | undefined): boolean => type === "whatsapp_cloud";

export const capabilitiesOf = (type: string | null | undefined): ChannelCapabilities | undefined =>
  type ? CAPACIDADES[type] : undefined;

/**
 * Lo que un front nuevo puede ofrecer contra un backend que todavía no tiene `GET /channels/types`:
 * sólo el canal QR, que es lo que ese backend sabe crear.
 */
export const TIPOS_DE_UN_BACKEND_VIEJO: ChannelTypeInfo[] = [{ type: "whatsapp", capabilities: WHATSAPP_WEB }];

/** Un BSUID de la Cloud API (`AR.1349…`, `AR.ENT.…`): no es un teléfono ni un nombre. */
export const esBsuid = (valor: string | null | undefined): boolean =>
  /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]+$/.test((valor ?? "").trim());

/**
 * El `externalThreadId` con que se abre una conversación con este teléfono (D20).
 *
 * - WhatsApp por QR: el JID, con el 9 de móvil de Argentina que el contacto guarda sin él.
 * - Cloud API: los dígitos tal como están. **No se sintetiza el 9**: el servidor busca por la clave
 *   canónica —`549X` y `54X` son la misma— y a un contacto se le contesta en la forma en que escribió.
 */
export function threadIdFor(type: string | null | undefined, phone: string): string {
  if (isCloudApi(type)) return (phone || "").replace(/^"|"$/g, "").replace(/\D/g, "");
  return toWhatsappJid(phone);
}

/** Cómo se muestra el hilo de alguien que todavía no tiene ficha. */
export function identifierFor(type: string | null | undefined, externalThreadId: string): string {
  if (isCloudApi(type) && esBsuid(externalThreadId)) return "Usuario de WhatsApp sin número visible";
  if (isWhatsappFamily(type)) return formatWhatsappPhone(externalThreadId);
  return externalThreadId;
}

// ── la ventana de 24 h (D27) ──────────────────────────────────────────────────────

/** Cuándo cierra la ventana, o `null` si el canal no tiene ventana o el contacto nunca escribió. */
export function cierreDeVentana(expiresAt: string | null | undefined): Date | null {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * ¿Se le puede escribir? Un tipo sin ventana, siempre. Uno con ventana, sólo si cierra en el futuro:
 * sin fecha es que el contacto todavía no escribió en vivo por este canal.
 */
export function ventanaAbierta(
  type: string | null | undefined,
  expiresAt: string | null | undefined,
  ahora: Date = new Date(),
): boolean {
  if (!capabilitiesOf(type)?.serviceWindowHours) return true;
  const cierre = cierreDeVentana(expiresAt);
  return !!cierre && cierre.getTime() > ahora.getTime();
}

/** "3 h 12 min", "8 min", "menos de un minuto": lo que falta para que cierre. */
export function tiempoRestante(expiresAt: string | null | undefined, ahora: Date = new Date()): string {
  const cierre = cierreDeVentana(expiresAt);
  if (!cierre) return "";
  const minutos = Math.floor((cierre.getTime() - ahora.getTime()) / 60000);
  if (minutos < 1) return "menos de un minuto";
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (!horas) return `${resto} min`;
  return resto ? `${horas} h ${resto} min` : `${horas} h`;
}

/**
 * El marcador que deja el servicio cuando Meta entrega un mensaje sin contenido que se pueda mostrar,
 * como uno mandado desde un dispositivo vinculado (131060). Espejo del conversation-service.
 */
/**
 * Los canales que se le pueden ofrecer a ese teléfono (D1, D27): uno de la Cloud API, sólo si el número
 * tiene la ventana de 24 h abierta. Devuelve aparte los que sacó, para que el selector diga por qué
 * faltan en vez de parecer que el canal desapareció.
 *
 * Sin teléfono no hay a quién responder, y un canal cuya consulta falla también se saca: ofrecerlo
 * sería prometer un envío que Meta rechaza.
 */
export async function ofreciblesPara<C extends { type: string }>(
  channels: C[],
  phone: string | null | undefined,
  conVentana: (channel: C, phone: string) => Promise<unknown>,
): Promise<{ channels: C[]; sinVentana: C[] }> {
  const cloud = channels.filter((c) => isCloudApi(c.type));
  if (!cloud.length) return { channels, sinVentana: [] };
  const abiertos = new Set<C>();
  if (phone) {
    await Promise.all(
      cloud.map(async (c) => {
        if (await conVentana(c, phone).catch(() => null)) abiertos.add(c);
      }),
    );
  }
  return {
    channels: channels.filter((c) => !isCloudApi(c.type) || abiertos.has(c)),
    sinVentana: cloud.filter((c) => !abiertos.has(c)),
  };
}

/** Por qué un canal de la Cloud API no se ofrece para iniciar una conversación. */
export const SIN_VENTANA =
  "Por WhatsApp Business sólo se le puede responder a quien escribió en las últimas 24 h.";

export const MARCADOR_VER_EN_EL_CELULAR = "[este mensaje no se puede mostrar acá: miralo en el celular]";

/** El código de un error del conversation-service: `ACCOUNT_RESTRICTED`, `OUTSIDE_SERVICE_WINDOW`, … */
export const codigoDeError = (e: unknown): string | undefined =>
  (e as { response?: { data?: { code?: string } } })?.response?.data?.code;
